/**
 * Excel import of the starting data (FR-PAR-46, FR-TRV-11, FR-TRV-26; Technical Specifications
 * 19.1 and 19.2): the templates and their columns, the reading of the cells, and the messages of
 * the validation report. The API matches the rows against the existing data and writes them; the
 * web application shows the report. Weighbridge files are mapped onto their templates by
 * import-mapping.ts (FR-TRV-27).
 */
import { z } from 'zod';
import { ASSIGNABLE_ROLES } from './administration.js';
import { AUTHORIZATION_KINDS, BSD_TYPES, CONDITIONNEMENT_CATEGORIES, CONSISTENCIES, LOCALES, PRESTATAIRE_TYPES, WASTE_TYPES, } from './enums.js';
import { EXPEDITION_COST_KINDS } from './costs.js';
import { INSTALLATION_KINDS } from './schemas.js';
export const IMPORT_KINDS = [
    'ORIGINS',
    'WASTES',
    'CONDITIONNEMENTS',
    'PRESTATAIRES',
    'AUTHORIZATIONS',
    'CAPS',
    'FILIERES',
    'USERS',
    'RETURNS',
    'INVOICES',
    'EXPEDITIONS',
    'STOCK_WEIGHINGS',
    'EXPEDITION_WEIGHINGS',
];
/** Language of a template: its sheet names, headers and the values written in its cells. */
export const IMPORT_LANGUAGES = ['fr', 'en'];
export function importLanguageOf(locale) {
    return locale === 'en' ? 'en' : 'fr';
}
/** Limits of an import file (Technical Specifications 19.2). */
export const IMPORT_MAX_BYTES = 20 * 1024 * 1024;
export const IMPORT_MAX_ROWS = 50_000;
/** Code pages of a CSV export: UTF-8, or those of the French and Arabic versions of Windows. */
export const IMPORT_ENCODINGS = ['utf-8', 'windows-1252', 'windows-1256'];
export const IMPORT_ACTIONS = ['CREATE', 'UPDATE', 'UNCHANGED', 'ERROR'];
/** A mapped file waits for its column mapping (FR-TRV-27) before it is checked. */
export const IMPORT_STATUSES = ['MAPPING', 'VALIDATED', 'COMMITTED', 'REVERSED'];
export const IMPORT_SEVERITIES = ['ERROR', 'WARNING', 'INFO'];
/** The file names a template accepts: a workbook, or a CSV, text or Excel export when mapped. */
export function importExtensions(template) {
    return template.mapped ? ['.csv', '.txt', '.xlsx'] : ['.xlsx'];
}
function options(values, labels) {
    return values.map((value) => ({ value, label: labels[value] }));
}
const WASTE_TYPE_LABELS = {
    DND: { fr: 'Non dangereux (DND)', en: 'Non-hazardous (DND)' },
    DD: { fr: 'Dangereux (DD)', en: 'Hazardous (DD)' },
    INERTE: { fr: 'Inerte', en: 'Inert' },
    DASRI: { fr: 'DASRI', en: 'Healthcare waste (DASRI)' },
    MATIERES_REVENDUES: { fr: 'Matières revendues', en: 'Resold materials' },
    RADIOACTIF: { fr: 'Radioactif', en: 'Radioactive' },
    DD_AMIANTE: { fr: 'DD amiante', en: 'Asbestos (DD)' },
    FLUIDES_FRIGORIGENES: { fr: 'Fluides frigorigènes', en: 'Refrigerant fluids' },
    TERRES_EXCAVEES: { fr: 'Terres excavées', en: 'Excavated soil' },
};
const CONSISTENCY_LABELS = {
    SOLIDE: { fr: 'Solide', en: 'Solid' },
    LIQUIDE: { fr: 'Liquide', en: 'Liquid' },
    PATEUX_LIQUIDE: { fr: 'Pâteux-liquide', en: 'Pasty-liquid' },
    PULVERULENT: { fr: 'Pulvérulent', en: 'Powdery' },
    GAZEUX: { fr: 'Gazeux', en: 'Gaseous' },
    PATEUX_SOLIDE: { fr: 'Pâteux-solide', en: 'Pasty-solid' },
};
const CATEGORY_LABELS = {
    AUTRE_COLIS: { fr: 'Autre colis', en: 'Other package' },
    BENNE: { fr: 'Benne', en: 'Skip' },
    RACKS: { fr: 'Racks', en: 'Racks' },
    CITERNE: { fr: 'Citerne', en: 'Tank' },
    AUTRE_VRAC: { fr: 'Autre vrac', en: 'Other bulk' },
    DOUBLE_SAC_GC_GRV: { fr: 'Double-sacs en GC ou GRV', en: 'Double bags in LC or IBC' },
    FUT: { fr: 'Fût', en: 'Drum' },
    GRV: { fr: 'GRV', en: 'IBC' },
    PALETTE_FILMEE: { fr: 'Palette filmée', en: 'Wrapped pallet' },
};
const PRESTATAIRE_TYPE_LABELS = {
    NEGOCIANT: { fr: 'Négociant', en: 'Trader' },
    COURTIER: { fr: 'Courtier', en: 'Broker' },
    TRANSPORTEUR_ROUTE: { fr: 'Transporteur route', en: 'Road carrier' },
    TRANSPORTEUR_RAIL: { fr: 'Transporteur rail', en: 'Rail carrier' },
    TRANSPORTEUR_MER: { fr: 'Transporteur mer', en: 'Sea carrier' },
    TRANSPORTEUR_AIR: { fr: 'Transporteur air', en: 'Air carrier' },
    TRANSPORTEUR_PIPE: { fr: 'Transporteur pipe', en: 'Pipeline carrier' },
    CENTRE_ENTREPOSAGE: { fr: "Centre d'entreposage", en: 'Storage centre' },
    CENTRE_TRAITEMENT: { fr: 'Centre de traitement', en: 'Treatment centre' },
    ENTREPRISE_TRAVAUX: { fr: 'Entreprise de travaux', en: 'Works contractor' },
};
const COST_KIND_TEXTS = {
    TRANSPORT: { fr: 'Transport', en: 'Transport' },
    TRAITEMENT: { fr: 'Traitement', en: 'Treatment' },
    TAXE: { fr: 'Taxe', en: 'Tax' },
    ACHAT_CONDITIONNEMENT: { fr: 'Achat de conditionnement', en: 'Packaging purchase' },
    RECETTE: { fr: 'Recette', en: 'Revenue' },
    AUTRE_OPERATION: { fr: 'Autre opération', en: 'Other operation' },
};
const INSTALLATION_KIND_LABELS = {
    TRAITEMENT: { fr: 'Traitement', en: 'Treatment' },
    ENTREPOSAGE: { fr: 'Entreposage', en: 'Storage' },
    TRANSIT_REGROUPEMENT: { fr: 'Transit / regroupement', en: 'Transit / consolidation' },
    DECHARGE_CLASSE_3: { fr: 'Décharge de classe 3', en: 'Class 3 landfill' },
};
const AUTHORIZATION_KIND_LABELS = {
    COLLECTE_TRANSPORT: { fr: 'Collecte et transport', en: 'Collection and transport' },
    INSTALLATION_TRAITEMENT: { fr: 'Installation de traitement', en: 'Treatment facility' },
    INSTALLATION_STOCKAGE: { fr: 'Installation de stockage', en: 'Storage facility' },
    DECHARGE_CLASSE_3: { fr: 'Décharge de classe 3', en: 'Class 3 landfill' },
    AUTRE: { fr: 'Autre', en: 'Other' },
};
const BSD_TYPE_LABELS = {
    BSD_DANGEREUX: { fr: 'BSD déchets dangereux', en: 'Hazardous waste tracking form' },
    BSD_NON_DANGEREUX: {
        fr: 'Bordereau déchets non dangereux',
        en: 'Non-hazardous waste form',
    },
    BSD_DASRI: { fr: 'BSD DASRI', en: 'Healthcare waste tracking form' },
    BON_ENLEVEMENT: { fr: "Bon d'enlèvement", en: 'Removal slip' },
    DOCUMENT_PERSONNALISE: { fr: 'Document personnalisé', en: 'Custom document' },
};
const ROLE_LABELS = {
    TENANT_ADMIN: { fr: 'Administrateur', en: 'Administrator' },
    GROUP_ANALYST: { fr: 'Analyste groupe', en: 'Group analyst' },
    SITE_ADMIN: { fr: 'Administrateur site', en: 'Site administrator' },
    SITE_MANAGER: { fr: 'Responsable déchets', en: 'Waste manager' },
    SITE_OPERATOR: { fr: 'Opérateur site', en: 'Site operator' },
    SITE_READER: { fr: 'Lecteur site', en: 'Site reader' },
    FINANCE: { fr: 'Finance', en: 'Finance' },
};
const LOCALE_LABELS = {
    fr: { fr: 'Français', en: 'French' },
    ar: { fr: 'Arabe', en: 'Arabic' },
    en: { fr: 'Anglais', en: 'English' },
};
const PACKING_GROUPS = ['I', 'II', 'III'].map((g) => ({
    value: g,
    label: { fr: g, en: g },
}));
const active = {
    key: 'active',
    label: { fr: 'Actif', en: 'Active' },
    type: 'boolean',
    help: {
        fr: 'Oui ou Non. Vide : Oui pour une nouvelle ligne, valeur inchangée sinon.',
        en: 'Yes or No. Blank: Yes for a new line, unchanged otherwise.',
    },
    width: 9,
};
const validFrom = {
    key: 'validFrom',
    label: { fr: 'Valable du', en: 'Valid from' },
    type: 'date',
    required: true,
    help: { fr: 'Date JJ/MM/AAAA.', en: 'Date DD/MM/YYYY.' },
    example: '01/01/2026',
    width: 12,
};
const validTo = {
    key: 'validTo',
    label: { fr: 'Valable au', en: 'Valid to' },
    type: 'date',
    required: true,
    help: {
        fr: 'Date JJ/MM/AAAA, au plus tôt la date de début.',
        en: 'Date DD/MM/YYYY, not before the start date.',
    },
    example: '31/12/2026',
    width: 12,
};
const installationHelp = {
    fr: "Nom de l'installation. Si deux prestataires ont une installation du même nom, écrivez « Prestataire / Installation ».",
    en: 'Name of the installation. When two providers have an installation of the same name, write "Provider / Installation".',
};
const prestataireRefHelp = {
    fr: 'Raison sociale ou ICE du prestataire, déjà créé dans la liste des prestataires.',
    en: 'Company name or ICE of the provider, already created in the provider list.',
};
// the columns weighbridge exports share (FR-TRV-27); the user maps theirs onto them
const weighingTicket = {
    key: 'ticket',
    label: { fr: 'N° de ticket', en: 'Ticket number' },
    type: 'text',
    required: true,
    identifies: true,
    aliases: [
        'Ticket',
        'N° ticket',
        'Numéro de ticket',
        'Bon de pesée',
        'N° de pesée',
        'N° pesée',
        'Pesée',
        'Weighing ticket',
        'Weighing number',
    ],
    help: {
        fr: "Le numéro que le pont-bascule donne à la pesée : une pesée déjà importée ne l'est pas deux fois.",
        en: 'The number the weighbridge gives the weighing: a weighing already imported is not imported twice.',
    },
    example: '000412',
    width: 14,
};
const weighedAt = {
    key: 'weighedAt',
    label: { fr: 'Date de pesée', en: 'Weighing date' },
    type: 'date',
    required: true,
    aliases: ['Date', 'Date et heure', 'Date/heure', 'Date de sortie', 'Date time', 'Weighed at'],
    help: {
        fr: "Avec ou sans l'heure : JJ/MM/AAAA HH:MM, ou dans l'ordre choisi pour le fichier.",
        en: 'With or without the time: DD/MM/YYYY HH:MM, or in the order chosen for the file.',
    },
    example: '31/10/2026 14:05',
    width: 16,
};
const netWeight = {
    key: 'netWeight',
    label: { fr: 'Poids net', en: 'Net weight' },
    type: 'number',
    aliases: ['Net', 'Net (kg)', 'Poids net (kg)', 'Masse nette', 'Net weight (kg)'],
    help: {
        fr: "En kilogrammes, ou en tonnes selon l'unité choisie ; à défaut, le brut moins la tare.",
        en: 'In kilograms, or in tonnes as chosen; when blank, the gross minus the tare.',
    },
    example: '12340',
    width: 12,
};
const grossWeight = {
    key: 'grossWeight',
    label: { fr: 'Poids brut', en: 'Gross weight' },
    type: 'number',
    aliases: [
        'Brut',
        'Brut (kg)',
        'Poids brut (kg)',
        'Poids en charge',
        'Gross',
        'Gross weight (kg)',
    ],
    help: {
        fr: 'Le poids en charge, dans la même unité.',
        en: 'The loaded weight, in the same unit.',
    },
    width: 12,
};
const tareWeight = {
    key: 'tareWeight',
    label: { fr: 'Tare', en: 'Tare' },
    type: 'number',
    aliases: ['Tare (kg)', 'Poids à vide', 'Poids vide', 'Empty weight'],
    help: { fr: 'Le poids à vide, dans la même unité.', en: 'The empty weight, in the same unit.' },
    width: 12,
};
export const IMPORT_TEMPLATES = {
    EXPEDITIONS: {
        kind: 'EXPEDITIONS',
        scope: 'SITE',
        sheet: { fr: 'Expéditions (historique)', en: 'Expéditions (history)' },
        match: {
            fr: "Chaque ligne crée une expédition passée dans l'état indiqué, avec son numéro de BSD d'origine, sans contrôle des règles du jour ni mouvement de stock : pour reprendre l'historique d'un site qui migre (FR-COL-30). Un BSD déjà présent sur le site n'est pas modifié.",
            en: 'Each line creates a past expédition in the state given, with its original BSD number, without the live rules nor any stock movement: to take over the history of a migrating site (FR-COL-30). A BSD already on the site is left alone.',
        },
        columns: [
            {
                key: 'bsdNumber',
                label: { fr: 'N° BSD', en: 'BSD number' },
                type: 'text',
                required: true,
                identifies: true,
                aliases: ['BSD', 'Bordereau', 'Numéro'],
                help: {
                    fr: "Le numéro porté par le bordereau d'origine.",
                    en: 'The number on the original bordereau.',
                },
                example: 'BSD-2024-0157',
                width: 20,
            },
            {
                key: 'shippedAt',
                label: { fr: "Date d'enlèvement", en: 'Pickup date' },
                type: 'date',
                required: true,
                aliases: ['Enlèvement', 'Date', 'Pickup'],
                help: { fr: 'AAAA-MM-JJ.', en: 'YYYY-MM-DD.' },
                example: '2024-03-12',
                width: 14,
            },
            {
                key: 'waste',
                label: { fr: 'Déchet', en: 'Waste' },
                type: 'text',
                required: true,
                aliases: ['Code CMD', 'CMD'],
                help: {
                    fr: 'Nom ou code CMD du déchet, déjà créé dans la liste des déchets du site.',
                    en: 'Name or CMD code of the waste, already created in the site waste list.',
                },
                example: '08 01 13',
                width: 30,
            },
            {
                key: 'quantityT',
                label: { fr: 'Quantité (t)', en: 'Quantity (t)' },
                type: 'number',
                required: true,
                aliases: ['Quantité', 'Quantity', 'Tonnes'],
                help: { fr: 'Quantité producteur en tonnes.', en: 'Producer quantity in tonnes.' },
                example: '1.25',
                width: 12,
            },
            {
                key: 'conditionnement',
                label: { fr: 'Conditionnement', en: 'Container' },
                type: 'text',
                help: {
                    fr: 'Nom du conditionnement du site, facultatif.',
                    en: 'Name of a site container, optional.',
                },
                width: 20,
            },
            {
                key: 'packages',
                label: { fr: 'Nombre de colis', en: 'Package count' },
                type: 'number',
                help: {
                    fr: 'Nombre de colis, avec le conditionnement.',
                    en: 'Number of packages, with the container.',
                },
                example: '5',
                width: 12,
            },
            {
                key: 'origin',
                label: { fr: 'Origine', en: 'Origin' },
                type: 'text',
                help: {
                    fr: "Nom de l'origine du site, facultatif.",
                    en: 'Name of a site origin, optional.',
                },
                width: 20,
            },
            {
                key: 'transporter',
                label: { fr: 'Transporteur', en: 'Carrier' },
                type: 'text',
                help: {
                    fr: 'Raison sociale ou ICE du transporteur ; vide pour un transport par vos soins.',
                    en: 'Company name or ICE of the carrier; blank when you carried the waste yourselves.',
                },
                example: 'Transports Atlas',
                width: 28,
            },
            {
                key: 'plateNumber',
                label: { fr: 'Immatriculation', en: 'Plate number' },
                type: 'text',
                help: { fr: 'Facultative.', en: 'Optional.' },
                width: 14,
            },
            {
                key: 'destination',
                label: { fr: 'Installation de destination', en: 'Destination installation' },
                type: 'text',
                help: {
                    fr: "Nom de l'installation, ou « Prestataire / Installation » ; vide pour un historique sans destination connue.",
                    en: 'Installation name, or "Provider / Installation"; blank when the destination is unknown.',
                },
                example: 'Cimenterie / Four 2',
                width: 30,
            },
            {
                key: 'drCode',
                label: { fr: 'Code D/R', en: 'D/R code' },
                type: 'text',
                help: {
                    fr: 'Opération de traitement (D1 à D15, R1 à R13).',
                    en: 'Treatment operation (D1 to D15, R1 to R13).',
                },
                example: 'R1',
                width: 10,
            },
            {
                key: 'status',
                label: { fr: 'Statut', en: 'Status' },
                type: 'enum',
                required: true,
                options: [
                    { value: 'SHIPPED', label: { fr: 'Enlevé', en: 'Shipped' } },
                    { value: 'RECEIVED', label: { fr: 'Reçu', en: 'Received' } },
                    { value: 'ACCEPTED', label: { fr: 'Accepté', en: 'Accepted' } },
                    { value: 'TREATED', label: { fr: 'Traité', en: 'Treated' } },
                ],
                help: { fr: "L'état atteint par l'expédition.", en: 'The state the expédition reached.' },
                width: 14,
            },
            {
                key: 'receivedAt',
                label: { fr: 'Date de réception', en: 'Reception date' },
                type: 'date',
                help: {
                    fr: 'Requise à partir du statut Reçu.',
                    en: 'Required from the Received status on.',
                },
                width: 14,
            },
            {
                key: 'quantityReceivedT',
                label: { fr: 'Quantité reçue (t)', en: 'Quantity received (t)' },
                type: 'number',
                help: {
                    fr: 'Quantité pesée par le destinataire ; la quantité producteur à défaut.',
                    en: 'Quantity weighed by the destination; the producer quantity when blank.',
                },
                width: 14,
            },
            {
                key: 'acceptedAt',
                label: { fr: "Date d'acceptation", en: 'Acceptance date' },
                type: 'date',
                help: {
                    fr: 'Requise à partir du statut Accepté ; la date de réception à défaut.',
                    en: 'Required from the Accepted status on; the reception date when blank.',
                },
                width: 14,
            },
            {
                key: 'treatedAt',
                label: { fr: 'Date de traitement', en: 'Treatment date' },
                type: 'date',
                help: { fr: 'Requise pour le statut Traité.', en: 'Required for the Treated status.' },
                width: 14,
            },
            {
                key: 'certificateNumber',
                label: { fr: 'N° de certificat', en: 'Certificate number' },
                type: 'text',
                help: {
                    fr: 'Certificat de traitement, facultatif.',
                    en: 'Treatment certificate, optional.',
                },
                width: 18,
            },
            {
                key: 'comment',
                label: { fr: 'Commentaire', en: 'Comment' },
                type: 'text',
                help: { fr: 'Facultatif.', en: 'Optional.' },
                width: 30,
            },
        ],
    },
    INVOICES: {
        kind: 'INVOICES',
        scope: 'TENANT',
        sheet: { fr: 'Factures prestataires', en: 'Provider invoices' },
        match: {
            fr: "Les lignes de même prestataire et numéro de facture forment une facture, créée à vérifier ; chaque ligne est rapprochée du coût du BSD indiqué (même nature, même prestataire). Une facture déjà enregistrée n'est pas modifiée.",
            en: 'Lines with the same provider and invoice number make one invoice, created for checking; each line is matched to the cost of the BSD given (same kind, same provider). An invoice already recorded is left alone.',
        },
        columns: [
            {
                key: 'prestataire',
                label: { fr: 'Prestataire', en: 'Service provider' },
                type: 'text',
                required: true,
                identifies: true,
                digits: 15,
                aliases: ['Raison sociale', 'Company name', 'Fournisseur', 'Supplier'],
                help: prestataireRefHelp,
                example: 'Recyclage Atlas SARL',
                width: 30,
            },
            {
                key: 'company',
                label: { fr: 'Société facturée', en: 'Billed company' },
                type: 'text',
                required: true,
                digits: 15,
                aliases: ['Société', 'Company'],
                help: {
                    fr: 'Raison sociale ou ICE de la société de votre organisation que la facture concerne.',
                    en: 'Legal name or ICE of the company of your organisation the invoice is addressed to.',
                },
                example: 'Industries Chaouia SA',
                width: 30,
            },
            {
                key: 'number',
                label: { fr: 'N° de facture', en: 'Invoice number' },
                type: 'text',
                required: true,
                identifies: true,
                aliases: ['Numéro', 'Number', 'Facture', 'Invoice'],
                help: {
                    fr: 'Le numéro porté par la facture du prestataire.',
                    en: "The number on the provider's invoice.",
                },
                example: 'F-2026-0412',
                width: 18,
            },
            {
                key: 'invoiceDate',
                label: { fr: 'Date de facture', en: 'Invoice date' },
                type: 'date',
                required: true,
                aliases: ['Date'],
                help: { fr: 'AAAA-MM-JJ.', en: 'YYYY-MM-DD.' },
                example: '2026-09-30',
                width: 14,
            },
            {
                key: 'dueDate',
                label: { fr: "Date d'échéance", en: 'Due date' },
                type: 'date',
                aliases: ['Échéance', 'Due'],
                help: { fr: 'AAAA-MM-JJ, facultative.', en: 'YYYY-MM-DD, optional.' },
                example: '2026-10-30',
                width: 14,
            },
            {
                key: 'total',
                label: { fr: 'Total facture (MAD)', en: 'Invoice total (MAD)' },
                type: 'number',
                required: true,
                aliases: ['Total', 'Montant total', 'Total amount'],
                help: {
                    fr: 'Le total de la facture, hors taxes, répété sur chacune de ses lignes.',
                    en: 'The invoice total, before tax, repeated on each of its lines.',
                },
                example: '12500',
                width: 18,
            },
            {
                key: 'bsdNumber',
                label: { fr: 'N° BSD', en: 'BSD number' },
                type: 'text',
                aliases: ['BSD', 'Bordereau'],
                help: {
                    fr: "Le numéro du BSD de l'expédition facturée ; vide pour une ligne sans expédition.",
                    en: 'The BSD number of the expédition invoiced; blank for a line without one.',
                },
                example: 'CAS-2026-000123',
                width: 20,
            },
            {
                key: 'kind',
                label: { fr: 'Nature', en: 'Kind' },
                type: 'enum',
                required: true,
                options: options(EXPEDITION_COST_KINDS, COST_KIND_TEXTS),
                help: { fr: 'La nature du coût facturé.', en: 'The kind of cost invoiced.' },
                width: 22,
            },
            {
                key: 'amount',
                label: { fr: 'Montant ligne (MAD)', en: 'Line amount (MAD)' },
                type: 'number',
                required: true,
                aliases: ['Montant', 'Amount'],
                help: {
                    fr: 'Le montant de la ligne, hors taxes ; négatif pour un avoir ou une recette.',
                    en: 'The line amount, before tax; negative for a credit or a revenue.',
                },
                example: '1250',
                width: 18,
            },
            {
                key: 'label',
                label: { fr: 'Libellé', en: 'Label' },
                type: 'text',
                aliases: ['Désignation', 'Description'],
                help: {
                    fr: 'Le libellé de la ligne sur la facture, facultatif.',
                    en: 'The line label on the invoice, optional.',
                },
                width: 30,
            },
        ],
    },
    ORIGINS: {
        kind: 'ORIGINS',
        scope: 'SITE',
        sheet: { fr: 'Origines', en: 'Origins' },
        match: {
            fr: "Une ligne met à jour l'origine de même chemin, sinon elle la crée.",
            en: 'A line updates the origin with the same path, otherwise it creates it.',
        },
        columns: [
            {
                key: 'path',
                label: { fr: 'Chemin', en: 'Path' },
                type: 'text',
                required: true,
                identifies: true,
                aliases: ['Origine', 'Origin'],
                help: {
                    fr: "Du site vers l'unité, séparé par « / » : Site / Secteur / Atelier. Le parent doit exister ou figurer dans le fichier.",
                    en: 'From the site down to the unit, separated by "/": Site / Sector / Workshop. The parent must exist or be in the file.',
                },
                example: 'Usine de Casablanca / Atelier peinture / Cabine 2',
                width: 48,
            },
            {
                key: 'collectionAddress',
                label: { fr: "Adresse d'enlèvement", en: 'Collection address' },
                type: 'text',
                help: {
                    fr: 'Adresse où le transporteur vient chercher les déchets.',
                    en: 'Where the carrier collects the waste.',
                },
                width: 32,
            },
            {
                key: 'parcelNumber',
                label: { fr: 'N° de parcelle', en: 'Parcel number' },
                type: 'text',
                help: { fr: 'Référence cadastrale.', en: 'Land registry reference.' },
                width: 16,
            },
            {
                key: 'costCenter',
                label: { fr: 'Centre de coût', en: 'Cost centre' },
                type: 'text',
                help: { fr: 'Centre de coût comptable.', en: 'Accounting cost centre.' },
                width: 16,
            },
            active,
        ],
    },
    WASTES: {
        kind: 'WASTES',
        scope: 'SITE',
        sheet: { fr: 'Déchets', en: 'Wastes' },
        match: {
            fr: 'Une ligne met à jour le déchet de même dénomination, sinon elle le crée.',
            en: 'A line updates the waste with the same name, otherwise it creates it.',
        },
        columns: [
            {
                key: 'name',
                label: { fr: 'Dénomination', en: 'Name' },
                type: 'text',
                required: true,
                identifies: true,
                aliases: ['Déchet', 'Waste', 'Libellé'],
                help: {
                    fr: 'Nom du déchet tel que le site le désigne ; au moins 10 caractères pour un code en 99 (BR-02).',
                    en: 'Name of the waste as the site calls it; at least 10 characters for a code ending in 99 (BR-02).',
                },
                example: 'Boues de peinture solvantées',
                width: 36,
            },
            {
                key: 'cmdCode',
                label: { fr: 'Code CMD', en: 'CMD code' },
                type: 'text',
                required: true,
                digits: 6,
                aliases: ['Code', 'Code déchet', 'Waste code'],
                help: {
                    fr: 'Code à six chiffres du Catalogue Marocain des Déchets, avec ou sans espaces.',
                    en: 'Six-digit code of the Moroccan Waste Catalogue, with or without spaces.',
                },
                example: '08 01 11*',
                width: 12,
            },
            {
                key: 'type',
                label: { fr: 'Type', en: 'Type' },
                type: 'enum',
                required: true,
                options: options(WASTE_TYPES, WASTE_TYPE_LABELS),
                help: {
                    fr: 'Doit correspondre au caractère dangereux du code, sinon justifiez-le (BR-01).',
                    en: 'Must match the hazardous flag of the code, otherwise justify it (BR-01).',
                },
                width: 20,
            },
            {
                key: 'typeJustification',
                label: { fr: 'Justification du type', en: 'Type justification' },
                type: 'text',
                help: {
                    fr: 'Obligatoire quand le type ne correspond pas au code (BR-01).',
                    en: 'Required when the type does not match the code (BR-01).',
                },
                width: 24,
            },
            {
                key: 'consistency',
                label: { fr: 'Consistance', en: 'Consistency' },
                type: 'enum',
                required: true,
                options: options(CONSISTENCIES, CONSISTENCY_LABELS),
                help: { fr: 'État physique du déchet.', en: 'Physical state of the waste.' },
                width: 14,
            },
            {
                key: 'internalNumber',
                label: { fr: 'N° interne', en: 'Internal number' },
                type: 'text',
                help: { fr: 'Référence interne du site.', en: 'Internal reference of the site.' },
                width: 14,
            },
            {
                key: 'pop',
                label: { fr: 'POP', en: 'POP' },
                type: 'boolean',
                help: {
                    fr: 'Oui si le déchet contient des polluants organiques persistants.',
                    en: 'Yes when the waste contains persistent organic pollutants.',
                },
                width: 7,
            },
            {
                key: 'traceabilityAlert',
                label: { fr: 'Alerte traçabilité', en: 'Traceability alert' },
                type: 'boolean',
                help: {
                    fr: 'Oui pour suivre le retour du bordereau et le traitement.',
                    en: 'Yes to follow the return of the form and the treatment.',
                },
                width: 12,
            },
            {
                key: 'unNumber',
                label: { fr: 'N° ONU', en: 'UN number' },
                type: 'text',
                digits: 4,
                aliases: ['ONU', 'UN'],
                help: {
                    fr: "Rend le déchet soumis à l'ADR ; le numéro doit figurer dans la table ADR.",
                    en: 'Makes the waste subject to ADR; the number must be in the ADR table.',
                },
                example: '1263',
                width: 9,
            },
            {
                key: 'packingGroup',
                label: { fr: "Groupe d'emballage", en: 'Packing group' },
                type: 'enum',
                options: PACKING_GROUPS,
                help: {
                    fr: 'I, II ou III, quand la table ADR en propose plusieurs pour ce n° ONU.',
                    en: 'I, II or III, when the ADR table lists several for this UN number.',
                },
                width: 10,
            },
            {
                key: 'technicalName',
                label: { fr: 'Nom technique', en: 'Technical name' },
                type: 'text',
                help: {
                    fr: "Complément de la désignation officielle, quand l'ADR l'exige (rubriques n.s.a.).",
                    en: 'Added to the proper shipping name when ADR requires it (n.o.s. entries).',
                },
                width: 22,
            },
            {
                key: 'environmentallyHazardous',
                label: { fr: "Dangereux pour l'environnement", en: 'Environmentally hazardous' },
                type: 'boolean',
                help: {
                    fr: "Oui pour ajouter la mention « dangereux pour l'environnement ».",
                    en: 'Yes to add the "environmentally hazardous" mention.',
                },
                width: 12,
            },
            {
                key: 'comment',
                label: { fr: 'Commentaire', en: 'Comment' },
                type: 'text',
                help: { fr: 'Texte libre.', en: 'Free text.' },
                width: 28,
            },
            active,
        ],
    },
    CONDITIONNEMENTS: {
        kind: 'CONDITIONNEMENTS',
        scope: 'SITE',
        sheet: { fr: 'Conditionnements', en: 'Containers' },
        match: {
            fr: 'Une ligne met à jour le conditionnement de même dénomination, sinon elle le crée.',
            en: 'A line updates the packaging with the same name, otherwise it creates it.',
        },
        columns: [
            {
                key: 'name',
                label: { fr: 'Dénomination', en: 'Name' },
                type: 'text',
                required: true,
                identifies: true,
                aliases: ['Conditionnement', 'Container', 'Packaging'],
                help: { fr: 'Nom du contenant.', en: 'Name of the container.' },
                example: 'Fût métallique 200 L',
                width: 30,
            },
            {
                key: 'category',
                label: { fr: 'Catégorie', en: 'Category' },
                type: 'enum',
                required: true,
                options: options(CONDITIONNEMENT_CATEGORIES, CATEGORY_LABELS),
                help: { fr: 'Catégorie du bordereau.', en: 'Category of the tracking form.' },
                width: 18,
            },
            {
                key: 'volumeL',
                label: { fr: 'Volume (L)', en: 'Volume (L)' },
                type: 'number',
                help: { fr: 'Contenance en litres.', en: 'Capacity in litres.' },
                example: '200',
                width: 11,
            },
            {
                key: 'packagingCode',
                label: { fr: "Code d'emballage", en: 'Packaging code' },
                type: 'text',
                help: {
                    fr: "Code ONU de l'emballage (1A2 fût acier, 31HA1 GRV), exigé pour les déchets ADR.",
                    en: 'UN packaging code (1A2 steel drum, 31HA1 IBC), required for ADR waste.',
                },
                example: '1A2',
                width: 12,
            },
            {
                key: 'tareKg',
                label: { fr: 'Tare (kg)', en: 'Tare (kg)' },
                type: 'number',
                help: { fr: 'Poids à vide en kilogrammes.', en: 'Empty weight in kilograms.' },
                width: 10,
            },
            {
                key: 'returnable',
                label: { fr: 'Consigné ou loué', en: 'Returnable or rented' },
                type: 'boolean',
                help: {
                    fr: 'Oui si le contenant est rendu au prestataire.',
                    en: 'Yes when the container goes back to the provider.',
                },
                width: 12,
            },
            active,
        ],
    },
    PRESTATAIRES: {
        kind: 'PRESTATAIRES',
        scope: 'TENANT',
        sheet: { fr: 'Prestataires', en: 'Service providers' },
        match: {
            fr: "Une ligne par installation (ou une seule ligne sans installation). Le prestataire est reconnu par son ICE, sinon par sa raison sociale et sa ville ; l'installation par son nom.",
            en: 'One line per installation (or a single line without installation). The provider is found by its ICE, otherwise by its company name and city; the installation by its name.',
        },
        columns: [
            {
                key: 'name',
                label: { fr: 'Raison sociale', en: 'Company name' },
                type: 'text',
                required: true,
                identifies: true,
                aliases: ['Prestataire', 'Service provider', 'Provider', 'Nom'],
                help: {
                    fr: 'Nom légal du prestataire, répété sur chaque ligne de ses installations.',
                    en: 'Legal name of the provider, repeated on the line of each installation.',
                },
                example: 'Recyclage Atlas SARL',
                width: 30,
            },
            {
                key: 'ice',
                label: { fr: 'ICE', en: 'ICE' },
                type: 'text',
                identifies: true,
                digits: 15,
                help: {
                    fr: 'Identifiant commun de l’entreprise, 15 chiffres (BR-25).',
                    en: 'Common company identifier, 15 digits (BR-25).',
                },
                example: '001234567000089',
                width: 18,
            },
            {
                key: 'rc',
                label: { fr: 'RC', en: 'RC' },
                type: 'text',
                help: { fr: 'Registre du commerce.', en: 'Trade register number.' },
                width: 10,
            },
            {
                key: 'if',
                label: { fr: 'IF', en: 'IF' },
                type: 'text',
                help: { fr: 'Identifiant fiscal, 7 ou 8 chiffres.', en: 'Tax identifier, 7 or 8 digits.' },
                width: 10,
            },
            {
                key: 'address',
                label: { fr: 'Adresse', en: 'Address' },
                type: 'text',
                help: { fr: 'Adresse du siège.', en: 'Head office address.' },
                width: 30,
            },
            {
                key: 'city',
                label: { fr: 'Ville', en: 'City' },
                type: 'text',
                identifies: true,
                help: { fr: 'Ville du siège.', en: 'Head office city.' },
                width: 14,
            },
            {
                key: 'types',
                label: { fr: 'Types', en: 'Types' },
                type: 'list',
                required: true,
                options: options(PRESTATAIRE_TYPES, PRESTATAIRE_TYPE_LABELS),
                help: {
                    fr: 'Un ou plusieurs types, séparés par des virgules.',
                    en: 'One or more types, separated by commas.',
                },
                example: 'Transporteur route, Centre de traitement',
                width: 30,
            },
            {
                key: 'installationName',
                label: { fr: 'Installation', en: 'Installation' },
                type: 'text',
                help: {
                    fr: 'Nom de l’installation ; vide pour un prestataire sans installation (transporteur).',
                    en: 'Name of the installation; blank for a provider without installation (carrier).',
                },
                example: 'Plateforme de Mohammedia',
                width: 26,
            },
            {
                key: 'installationKind',
                label: { fr: "Type d'installation", en: 'Installation kind' },
                type: 'enum',
                options: options(INSTALLATION_KINDS, INSTALLATION_KIND_LABELS),
                help: {
                    fr: 'Obligatoire pour une nouvelle installation.',
                    en: 'Required for a new installation.',
                },
                width: 20,
            },
            {
                key: 'installationAddress',
                label: { fr: "Adresse de l'installation", en: 'Installation address' },
                type: 'text',
                help: { fr: 'Adresse du site de l’installation.', en: 'Address of the installation.' },
                width: 30,
            },
            {
                key: 'installationCity',
                label: { fr: "Ville de l'installation", en: 'Installation city' },
                type: 'text',
                help: { fr: 'Ville de l’installation.', en: 'City of the installation.' },
                width: 14,
            },
            {
                key: 'drCodes',
                label: { fr: 'Codes D/R', en: 'D/R codes' },
                type: 'list',
                help: {
                    fr: "Opérations réalisées par l'installation, séparées par des virgules (D1 à D15, R1 à R13).",
                    en: 'Operations the installation performs, separated by commas (D1 to D15, R1 to R13).',
                },
                example: 'R1, R12, D10',
                width: 16,
            },
            {
                key: 'activeOnSite',
                label: { fr: 'Activé sur le site', en: 'Enabled on the site' },
                type: 'boolean',
                help: {
                    fr: 'Oui pour activer le prestataire sur le site choisi. Vide : Oui pour un nouveau prestataire, inchangé sinon.',
                    en: 'Yes to enable the provider on the selected site. Blank: Yes for a new provider, unchanged otherwise.',
                },
                width: 12,
            },
        ],
    },
    AUTHORIZATIONS: {
        kind: 'AUTHORIZATIONS',
        scope: 'TENANT',
        sheet: { fr: 'Autorisations', en: 'Authorizations' },
        match: {
            fr: "Une ligne met à jour l'autorisation de même prestataire, type et numéro, sinon elle la crée.",
            en: 'A line updates the authorization with the same provider, kind and number, otherwise it creates it.',
        },
        columns: [
            {
                key: 'prestataire',
                label: { fr: 'Prestataire', en: 'Service provider' },
                type: 'text',
                required: true,
                identifies: true,
                digits: 15,
                aliases: ['Raison sociale', 'Company name'],
                help: prestataireRefHelp,
                example: 'Recyclage Atlas SARL',
                width: 30,
            },
            {
                key: 'installation',
                label: { fr: 'Installation', en: 'Installation' },
                type: 'text',
                help: {
                    fr: "Installation couverte ; vide quand l'autorisation vaut pour le prestataire (collecte-transport).",
                    en: 'Installation covered; blank when the authorization is for the provider (collection and transport).',
                },
                width: 26,
            },
            {
                key: 'kind',
                label: { fr: "Type d'autorisation", en: 'Authorization kind' },
                type: 'enum',
                required: true,
                identifies: true,
                options: options(AUTHORIZATION_KINDS, AUTHORIZATION_KIND_LABELS),
                help: { fr: 'Nature de l’autorisation.', en: 'Kind of authorization.' },
                width: 24,
            },
            {
                key: 'number',
                label: { fr: 'Numéro', en: 'Number' },
                type: 'text',
                required: true,
                identifies: true,
                help: { fr: 'Numéro de l’arrêté ou du récépissé.', en: 'Number of the order or receipt.' },
                example: 'AUT-2026-014',
                width: 18,
            },
            {
                key: 'issuingAuthority',
                label: { fr: 'Autorité de délivrance', en: 'Issuing authority' },
                type: 'text',
                help: { fr: 'Administration qui l’a délivrée.', en: 'Administration that issued it.' },
                width: 24,
            },
            validFrom,
            validTo,
            {
                key: 'cmdCodes',
                label: { fr: 'Codes CMD couverts', en: 'CMD codes covered' },
                type: 'list',
                help: {
                    fr: 'Codes ou chapitres couverts, séparés par des virgules ; vide : tous.',
                    en: 'Codes or chapters covered, separated by commas; blank: all.',
                },
                example: '13, 16 06 01*',
                width: 22,
            },
            {
                key: 'notes',
                label: { fr: 'Notes', en: 'Notes' },
                type: 'text',
                help: { fr: 'Texte libre.', en: 'Free text.' },
                width: 24,
            },
        ],
    },
    CAPS: {
        kind: 'CAPS',
        scope: 'SITE',
        sheet: { fr: 'CAP', en: 'CAP' },
        match: {
            fr: 'Une ligne met à jour le CAP de même déchet, installation et numéro, sinon elle le crée.',
            en: 'A line updates the CAP with the same waste, installation and number, otherwise it creates it.',
        },
        columns: [
            {
                key: 'waste',
                label: { fr: 'Déchet', en: 'Waste' },
                type: 'text',
                required: true,
                identifies: true,
                aliases: ['Dénomination'],
                help: {
                    fr: 'Dénomination du déchet, déjà créé sur le site.',
                    en: 'Name of the waste, already created on the site.',
                },
                width: 32,
            },
            {
                key: 'prestataire',
                label: { fr: 'Prestataire', en: 'Service provider' },
                type: 'text',
                digits: 15,
                help: {
                    fr: 'Raison sociale ou ICE ; utile quand deux prestataires ont une installation du même nom.',
                    en: 'Company name or ICE; useful when two providers have an installation of the same name.',
                },
                width: 26,
            },
            {
                key: 'installation',
                label: { fr: 'Installation', en: 'Installation' },
                type: 'text',
                required: true,
                identifies: true,
                help: installationHelp,
                width: 26,
            },
            {
                key: 'number',
                label: { fr: 'N° CAP', en: 'CAP number' },
                type: 'text',
                required: true,
                identifies: true,
                aliases: ['Numéro', 'Number'],
                help: {
                    fr: "Numéro du certificat d'acceptation préalable.",
                    en: 'Number of the prior acceptance certificate.',
                },
                example: 'CAP-2026-031',
                width: 16,
            },
            validFrom,
            validTo,
            {
                key: 'description',
                label: { fr: 'Description', en: 'Description' },
                type: 'text',
                help: { fr: 'Texte libre.', en: 'Free text.' },
                width: 28,
            },
            {
                key: 'acceptedAt',
                label: { fr: 'Accepté le', en: 'Accepted on' },
                type: 'date',
                help: {
                    fr: "Date du volet B signé par l'installation ; vide : CAP en attente d'acceptation, inutilisable.",
                    en: 'Date of part B signed by the installation; blank: CAP awaiting acceptance, not usable.',
                },
                width: 12,
            },
            {
                key: 'treatmentCodes',
                label: { fr: 'Traitements (codes D/R)', en: 'Treatments (D/R codes)' },
                type: 'list',
                help: {
                    fr: "Traitements acceptés au volet B ; vide : les codes D/R de l'installation.",
                    en: 'Treatments accepted in part B; blank: the D/R codes of the installation.',
                },
                example: 'R1',
                width: 14,
            },
        ],
    },
    RETURNS: {
        kind: 'RETURNS',
        scope: 'SITE',
        sheet: { fr: 'Retours de BSD', en: 'BSD returns' },
        match: {
            fr: "Une ligne enregistre la réception, et l'acceptation si sa date est donnée, du BSD de ce numéro (le numéro du site ou celui du prestataire). Le modèle contient les BSD dont le retour est attendu.",
            en: "A line records the reception, and the acceptance when its date is given, of the BSD with that number (the site's or the prestataire's). The template lists the BSD whose return is awaited.",
        },
        columns: [
            {
                key: 'bsdNumber',
                label: { fr: 'N° BSD', en: 'BSD number' },
                type: 'text',
                required: true,
                identifies: true,
                aliases: ['BSD', 'Bordereau', 'N° de bordereau'],
                help: {
                    fr: 'Numéro du bordereau, celui du site ou celui porté par le prestataire.',
                    en: "Number of the tracking form, the site's or the one the prestataire wrote.",
                },
                example: '202609-00012',
                width: 18,
            },
            {
                key: 'shippedAt',
                label: { fr: 'Enlevé le', en: 'Picked up on' },
                type: 'date',
                help: { fr: 'Pour information ; non relu.', en: 'For information; not read.' },
                width: 12,
            },
            {
                key: 'wasteName',
                label: { fr: 'Déchet', en: 'Waste' },
                type: 'text',
                help: { fr: 'Pour information ; non relu.', en: 'For information; not read.' },
                width: 28,
            },
            {
                key: 'quantityT',
                label: { fr: 'Quantité producteur (t)', en: 'Producer quantity (t)' },
                type: 'number',
                help: { fr: 'Pour information ; non relue.', en: 'For information; not read.' },
                width: 14,
            },
            {
                key: 'receivedAt',
                label: { fr: 'Reçu le', en: 'Received on' },
                type: 'date',
                required: true,
                help: {
                    fr: "Date de réception par l'installation, au plus tôt le jour de l'enlèvement.",
                    en: 'Date of reception by the installation, not before the pickup.',
                },
                width: 12,
            },
            {
                key: 'quantityReceivedT',
                label: { fr: 'Quantité reçue (t)', en: 'Received quantity (t)' },
                type: 'number',
                required: true,
                help: {
                    fr: 'Quantité pesée à la réception ; un écart au-delà de la tolérance du site demande un commentaire (BR-20).',
                    en: "Quantity weighed at reception; a gap beyond the site's tolerance needs a comment (BR-20).",
                },
                width: 14,
            },
            {
                key: 'acceptedAt',
                label: { fr: 'Accepté le', en: 'Accepted on' },
                type: 'date',
                help: {
                    fr: "Date de l'acceptation signée ; vide : réception seule, le retour reste attendu.",
                    en: 'Date of the signed acceptance; blank: reception only, the return stays awaited.',
                },
                width: 12,
            },
            {
                key: 'quantityRefusedT',
                label: { fr: 'Quantité refusée (t)', en: 'Refused quantity (t)' },
                type: 'number',
                help: {
                    fr: "Partie refusée à l'acceptation, avec son motif ; vide ou 0 : tout accepté.",
                    en: 'Part refused at acceptance, with its reason; blank or 0: all accepted.',
                },
                width: 14,
            },
            {
                key: 'refusalReason',
                label: { fr: 'Motif du refus', en: 'Refusal reason' },
                type: 'text',
                help: {
                    fr: 'Obligatoire avec une quantité refusée.',
                    en: 'Required with a refused quantity.',
                },
                width: 28,
            },
            {
                key: 'comment',
                label: { fr: 'Commentaire', en: 'Comment' },
                type: 'text',
                help: {
                    fr: "Commentaire de réception, par exemple l'explication d'un écart de quantité.",
                    en: 'Reception comment, for example the explanation of a quantity gap.',
                },
                width: 32,
            },
        ],
    },
    FILIERES: {
        kind: 'FILIERES',
        scope: 'SITE',
        sheet: { fr: 'Filières', en: 'Routes' },
        match: {
            fr: 'Une ligne par déchet : elle crée ou met à jour sa filière par défaut.',
            en: 'One line per waste: it creates or updates its default route.',
        },
        columns: [
            {
                key: 'waste',
                label: { fr: 'Déchet', en: 'Waste' },
                type: 'text',
                required: true,
                identifies: true,
                aliases: ['Dénomination'],
                help: {
                    fr: 'Dénomination du déchet, déjà créé sur le site.',
                    en: 'Name of the waste, already created on the site.',
                },
                width: 32,
            },
            {
                key: 'conditionnement',
                label: { fr: 'Conditionnement', en: 'Container' },
                type: 'text',
                help: { fr: 'Dénomination du conditionnement.', en: 'Name of the packaging.' },
                width: 22,
            },
            {
                key: 'origin',
                label: { fr: 'Origine', en: 'Origin' },
                type: 'text',
                help: {
                    fr: "Chemin de l'origine, comme dans le modèle des origines.",
                    en: 'Path of the origin, as in the origins template.',
                },
                width: 30,
            },
            {
                key: 'transporter',
                label: { fr: 'Transporteur', en: 'Carrier' },
                type: 'text',
                digits: 15,
                help: prestataireRefHelp,
                width: 26,
            },
            {
                key: 'transporterAuthorization',
                label: {
                    fr: 'Autorisation de collecte-transport',
                    en: 'Collection and transport authorization',
                },
                type: 'text',
                help: {
                    fr: "Numéro de l'autorisation du transporteur ; vide : celle en vigueur à chaque expédition.",
                    en: "Number of the carrier's authorization; blank: the one valid at each shipment.",
                },
                width: 20,
            },
            {
                key: 'storageInstallation',
                label: { fr: 'Entreposage intermédiaire', en: 'Intermediate storage' },
                type: 'text',
                help: installationHelp,
                width: 26,
            },
            {
                key: 'treatmentInstallation',
                label: { fr: 'Installation de traitement', en: 'Treatment installation' },
                type: 'text',
                help: installationHelp,
                width: 26,
            },
            {
                key: 'drCode',
                label: { fr: 'Code D/R', en: 'D/R code' },
                type: 'text',
                help: {
                    fr: "Opération de traitement, réalisée par l'installation de traitement.",
                    en: 'Treatment operation, performed by the treatment installation.',
                },
                example: 'R1',
                width: 9,
            },
            {
                key: 'treatmentLabel',
                label: { fr: 'Libellé du traitement', en: 'Treatment label' },
                type: 'text',
                help: { fr: 'Texte imprimé sur le bordereau.', en: 'Text printed on the form.' },
                width: 24,
            },
            {
                key: 'bsdType',
                label: { fr: 'Type de bordereau', en: 'Form type' },
                type: 'enum',
                options: options(BSD_TYPES, BSD_TYPE_LABELS),
                help: {
                    fr: 'Vide : BSD déchets dangereux pour un déchet dangereux, bordereau non dangereux sinon.',
                    en: 'Blank: hazardous waste form for a hazardous waste, non-hazardous form otherwise.',
                },
                width: 24,
            },
        ],
    },
    USERS: {
        kind: 'USERS',
        scope: 'TENANT',
        sheet: { fr: 'Utilisateurs', en: 'Users' },
        match: {
            fr: "Une ligne par rôle. L'utilisateur est reconnu par son e-mail ; un nouvel e-mail reçoit une invitation. Les rôles absents du fichier sont conservés.",
            en: 'One line per role. The user is found by e-mail; a new e-mail receives an invitation. Roles missing from the file are kept.',
        },
        columns: [
            {
                key: 'email',
                label: { fr: 'E-mail', en: 'E-mail' },
                type: 'text',
                required: true,
                identifies: true,
                aliases: ['Email', 'Courriel', 'Adresse e-mail'],
                help: { fr: 'Adresse de connexion.', en: 'Sign-in address.' },
                example: 'prenom.nom@exemple.ma',
                width: 30,
            },
            {
                key: 'firstName',
                label: { fr: 'Prénom', en: 'First name' },
                type: 'text',
                required: true,
                help: { fr: 'Obligatoire pour un nouvel utilisateur.', en: 'Required for a new user.' },
                width: 16,
            },
            {
                key: 'lastName',
                label: { fr: 'Nom', en: 'Last name' },
                type: 'text',
                required: true,
                help: { fr: 'Obligatoire pour un nouvel utilisateur.', en: 'Required for a new user.' },
                width: 16,
            },
            {
                key: 'locale',
                label: { fr: 'Langue', en: 'Language' },
                type: 'enum',
                options: options(LOCALES, LOCALE_LABELS),
                help: { fr: 'Vide : français.', en: 'Blank: French.' },
                width: 10,
            },
            {
                key: 'role',
                label: { fr: 'Rôle', en: 'Role' },
                type: 'enum',
                required: true,
                options: options(ASSIGNABLE_ROLES, ROLE_LABELS),
                help: { fr: 'Un rôle par ligne.', en: 'One role per line.' },
                width: 20,
            },
            {
                key: 'scope',
                label: { fr: 'Site ou société', en: 'Site or company' },
                type: 'text',
                aliases: ['Site', 'Périmètre', 'Scope'],
                help: {
                    fr: "Nom du site ou de la société ; vide pour toute l'organisation (administrateur, analyste groupe, finance).",
                    en: 'Name of the site or company; blank for the whole organisation (administrator, group analyst, finance).',
                },
                width: 26,
            },
            {
                key: 'expiresOn',
                label: { fr: "Jusqu'au", en: 'Until' },
                type: 'date',
                help: {
                    fr: 'Dernier jour du rôle, pour un accès temporaire ; vide : sans limite.',
                    en: 'Last day of the role, for temporary access; blank: no end.',
                },
                width: 12,
            },
        ],
    },
    STOCK_WEIGHINGS: {
        kind: 'STOCK_WEIGHINGS',
        scope: 'SITE',
        mapped: true,
        sheet: { fr: 'Pesées de production', en: 'Production weighings' },
        match: {
            fr: "Chaque pesée devient une entrée de production dans le stock du site, avec son ticket, comme une saisie à l'écran (FR-COL-08). Un ticket déjà en stock n'est pas importé deux fois.",
            en: 'Each weighing becomes a production entry in the site stock, with its ticket, as if typed on the screen (FR-COL-08). A ticket already in stock is not imported twice.',
        },
        columns: [
            weighingTicket,
            weighedAt,
            {
                key: 'product',
                label: { fr: 'Produit', en: 'Product' },
                type: 'text',
                aliases: ['Déchet', 'Article', 'Matière', 'Désignation', 'Code produit', 'Waste'],
                help: {
                    fr: 'Le produit pesé : un déchet du site par la correspondance, sinon par son nom, son code CMD ou son numéro interne, sinon le déchet par défaut.',
                    en: 'The product weighed: a site waste through the mapping, otherwise by its name, CMD code or internal number, otherwise the default waste.',
                },
                example: 'DIB',
                width: 24,
            },
            netWeight,
            grossWeight,
            tareWeight,
            {
                key: 'origin',
                label: { fr: 'Origine', en: 'Origin' },
                type: 'text',
                aliases: ['Atelier', 'Secteur', 'Provenance', 'Workshop'],
                help: {
                    fr: "Nom d'une origine du site, sinon l'origine par défaut ; facultative.",
                    en: 'Name of a site origin, otherwise the default origin; optional.',
                },
                width: 20,
            },
            {
                key: 'conditionnement',
                label: { fr: 'Conditionnement', en: 'Container' },
                type: 'text',
                aliases: ['Contenant', 'Emballage'],
                help: {
                    fr: "Nom d'un conditionnement du site, sinon celui par défaut ; facultatif.",
                    en: 'Name of a site container, otherwise the default one; optional.',
                },
                width: 20,
            },
            {
                key: 'comment',
                label: { fr: 'Commentaire', en: 'Comment' },
                type: 'text',
                aliases: ['Observation', 'Observations', 'Remarque', 'Note'],
                help: {
                    fr: 'Facultatif ; inscrit au registre comme motif, à la place du ticket.',
                    en: 'Optional; written in the registre as the reason, instead of the ticket.',
                },
                width: 30,
            },
        ],
    },
    EXPEDITION_WEIGHINGS: {
        kind: 'EXPEDITION_WEIGHINGS',
        scope: 'SITE',
        mapped: true,
        sheet: { fr: 'Pesées des expéditions', en: 'Expédition weighings' },
        match: {
            fr: "Chaque pesée d'un camion qui part donne sa quantité réelle au BSD du site qu'il emporte, trouvé par son numéro, sinon par l'immatriculation et la date : avant l'enlèvement la quantité du BSD change, après elle le BSD est révisé.",
            en: "Each weighing of a truck leaving the site gives its real quantity to the site's BSD it carries, found by its number, otherwise by the plate and the date: before the pickup the BSD's quantity changes, after it the BSD is revised.",
        },
        columns: [
            weighingTicket,
            weighedAt,
            {
                key: 'bsdNumber',
                label: { fr: 'N° BSD', en: 'BSD number' },
                type: 'text',
                aliases: ['BSD', 'Bordereau', 'N° bordereau', 'Référence', 'Reference'],
                help: {
                    fr: "Le numéro du BSD du site, ou celui du prestataire ; à défaut, l'immatriculation et la date retrouvent le chargement.",
                    en: "The site's BSD number, or the provider's; when blank, the plate and the date find the chargement.",
                },
                example: 'CAS-2026-000123',
                width: 20,
            },
            {
                key: 'plateNumber',
                label: { fr: 'Immatriculation', en: 'Plate number' },
                type: 'text',
                aliases: ['Matricule', 'Immat', 'Camion', 'Véhicule', 'Plaque', 'Truck', 'Vehicle'],
                help: {
                    fr: "L'immatriculation du camion, en lettres latines ou arabes : un seul chargement du site doit partir avec ce camion ce jour-là.",
                    en: 'The truck plate, in Latin or Arabic letters: one chargement of the site must leave with that truck on that day.',
                },
                example: '12345-A-6',
                width: 14,
            },
            netWeight,
            grossWeight,
            tareWeight,
            {
                key: 'comment',
                label: { fr: 'Commentaire', en: 'Comment' },
                type: 'text',
                aliases: ['Observation', 'Observations', 'Remarque', 'Note'],
                help: {
                    fr: "Facultatif ; ajouté au motif de la révision ou à l'historique du BSD.",
                    en: "Optional; added to the revision's reason or to the BSD's history.",
                },
                width: 30,
            },
        ],
    },
};
export function toImportCell(value) {
    if (value === null || value === undefined)
        return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
    }
    if (typeof value === 'string') {
        const s = value.trim();
        return s === '' ? null : s;
    }
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    if (typeof value === 'boolean')
        return value;
    return String(value);
}
/** Lower case, without accents or punctuation: how headers, names and values are compared. */
export function normalizeText(s) {
    return s
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
}
/** A path written "A / B / C" (or with ">"), as its normalised segments. */
export function pathSegments(path) {
    return path
        .split(/[/>\\]/)
        .map((s) => s.trim())
        .filter(Boolean);
}
export function cellText(cell) {
    if (cell === null)
        return '';
    if (typeof cell === 'boolean')
        return cell ? 'Oui' : 'Non';
    return String(cell);
}
const TRUE_WORDS = new Set(['oui', 'o', 'yes', 'y', 'true', 'vrai', '1', 'x', 'نعم']);
const FALSE_WORDS = new Set(['non', 'n', 'no', 'false', 'faux', '0', 'لا']);
function matchOption(opts, raw) {
    const n = normalizeText(raw);
    return opts.find((o) => normalizeText(o.value) === n ||
        normalizeText(o.label.fr) === n ||
        normalizeText(o.label.en) === n)?.value;
}
/** Excel stores days since 30 December 1899 when a date cell has no date format. */
function serialDate(n) {
    if (!Number.isInteger(n) || n < 1 || n > 2958465)
        return undefined;
    return new Date(Date.UTC(1899, 11, 30) + n * 86400000).toISOString().slice(0, 10);
}
function isoDate(y, m, d) {
    const date = new Date(Date.UTC(y, m - 1, d));
    if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) {
        return undefined;
    }
    return date.toISOString().slice(0, 10);
}
export function parseImportDate(cell) {
    if (typeof cell === 'number')
        return serialDate(cell);
    if (typeof cell !== 'string')
        return undefined;
    const s = cell.trim();
    let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
    if (m)
        return isoDate(Number(m[1]), Number(m[2]), Number(m[3]));
    m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s);
    if (m)
        return isoDate(Number(m[3]), Number(m[2]), Number(m[1]));
    return undefined;
}
export function parseImportNumber(cell) {
    if (typeof cell === 'number')
        return cell;
    if (typeof cell !== 'string')
        return undefined;
    const s = cell.replace(/[\s  ]/g, '').replace(',', '.');
    return /^-?\d+(\.\d+)?$/.test(s) ? Number(s) : undefined;
}
/** A cell read for its column: undefined when blank, or the problem that makes it unreadable. */
export function readImportCell(column, cell) {
    if (cell === null || (typeof cell === 'string' && cell.trim() === '')) {
        return { value: undefined };
    }
    switch (column.type) {
        case 'text': {
            if (typeof cell === 'number' && column.digits && Number.isInteger(cell)) {
                const digits = String(cell);
                return {
                    value: digits.length < column.digits ? digits.padStart(column.digits, '0') : digits,
                };
            }
            return { value: cellText(cell).trim() };
        }
        case 'number': {
            const n = parseImportNumber(cell);
            return n === undefined ? { problem: { code: 'NUMBER' } } : { value: n };
        }
        case 'boolean': {
            if (typeof cell === 'boolean')
                return { value: cell };
            const word = normalizeText(String(cell));
            if (TRUE_WORDS.has(word))
                return { value: true };
            if (FALSE_WORDS.has(word))
                return { value: false };
            return { problem: { code: 'BOOLEAN' } };
        }
        case 'date': {
            const d = parseImportDate(cell);
            return d ? { value: d } : { problem: { code: 'DATE' } };
        }
        case 'enum': {
            const v = matchOption(column.options ?? [], cellText(cell));
            return v ? { value: v } : { problem: { code: 'ENUM' } };
        }
        case 'list': {
            const items = cellText(cell)
                .split(/[,;\n|]+/)
                .map((s) => s.trim())
                .filter(Boolean);
            if (!column.options)
                return { value: items };
            const values = [];
            for (const item of items) {
                const v = matchOption(column.options, item);
                if (!v)
                    return { problem: { code: 'ENUM_ITEM', params: { item } } };
                if (!values.includes(v))
                    values.push(v);
            }
            return { value: values };
        }
    }
}
/** The headers a column answers to, normalised: its key, its labels and its aliases. */
export function importColumnNames(column) {
    return new Set([column.key, column.label.fr, column.label.en, ...(column.aliases ?? [])].map(normalizeText));
}
/**
 * The rows of the first sheet under its header line, keyed by column. Headers are matched
 * without case, accents or punctuation, in French or English (a required mark "*" is ignored);
 * blank rows are skipped. A missing identifying column is an error of the whole file.
 */
export function readImportSheet(template, data) {
    const issues = [];
    const names = template.columns.map((c) => ({ column: c, names: importColumnNames(c) }));
    const textOf = (v) => cellText(toImportCell(v)).trim();
    // the header is the first line naming at least one column of the template
    let headerIndex = data.findIndex((line) => (line ?? []).some((v) => {
        const n = normalizeText(textOf(v));
        return n && names.some((c) => c.names.has(n));
    }));
    if (headerIndex < 0)
        headerIndex = data.findIndex((line) => (line ?? []).some((v) => textOf(v)));
    if (headerIndex < 0) {
        return {
            headers: {},
            rows: [],
            issues: [{ row: null, code: 'FILE_EMPTY', severity: 'ERROR' }],
        };
    }
    const index = {};
    const headers = {};
    (data[headerIndex] ?? []).forEach((v, i) => {
        const text = textOf(v);
        if (!text)
            return;
        const match = names.find((c) => c.names.has(normalizeText(text)));
        if (!match) {
            issues.push({
                row: headerIndex + 1,
                code: 'UNKNOWN_COLUMN',
                params: { header: text },
                severity: 'WARNING',
            });
            return;
        }
        if (index[match.column.key] !== undefined) {
            issues.push({
                row: headerIndex + 1,
                column: match.column.key,
                code: 'DUPLICATE_COLUMN',
                params: { header: text },
                severity: 'WARNING',
            });
            return;
        }
        index[match.column.key] = i;
        headers[match.column.key] = text;
    });
    for (const c of template.columns) {
        if (c.identifies && c.required && index[c.key] === undefined) {
            issues.push({
                row: null,
                column: c.key,
                code: 'MISSING_COLUMN',
                params: { header: c.label.fr, headerEn: c.label.en },
                severity: 'ERROR',
            });
        }
    }
    const rows = [];
    for (let i = headerIndex + 1; i < data.length; i += 1) {
        const line = data[i] ?? [];
        const cells = {};
        let blank = true;
        for (const [key, at] of Object.entries(index)) {
            const cell = toImportCell(line[at]);
            cells[key] = cell;
            if (cell !== null)
                blank = false;
        }
        if (!blank)
            rows.push({ row: i + 1, cells });
    }
    if (!rows.length && !issues.some((x) => x.severity === 'ERROR')) {
        issues.push({ row: null, code: 'FILE_EMPTY', severity: 'ERROR' });
    }
    if (rows.length > IMPORT_MAX_ROWS) {
        issues.push({
            row: null,
            code: 'FILE_TOO_MANY_ROWS',
            params: { max: IMPORT_MAX_ROWS },
            severity: 'ERROR',
        });
    }
    return { headers, rows, issues };
}
/**
 * The values of a row for its template: blank cells are undefined, readable cells typed, and
 * unreadable ones reported (the row cannot be imported).
 */
export function readImportRow(template, row) {
    const values = {};
    const issues = [];
    for (const column of template.columns) {
        if (!(column.key in row.cells))
            continue;
        const read = readImportCell(column, row.cells[column.key] ?? null);
        if (read.problem) {
            issues.push({
                row: row.row,
                column: column.key,
                code: read.problem.code,
                params: read.problem.params,
                severity: 'ERROR',
            });
        }
        else if (read.value !== undefined) {
            values[column.key] = read.value;
        }
    }
    return { values, issues };
}
// ---- report ------------------------------------------------------------------------------------
export const IMPORT_ISSUE_CODES = [
    'FILE_EMPTY',
    'FILE_TOO_MANY_ROWS',
    'MISSING_COLUMN',
    'UNKNOWN_COLUMN',
    'DUPLICATE_COLUMN',
    'REQUIRED',
    'NUMBER',
    'BOOLEAN',
    'DATE',
    'ENUM',
    'ENUM_ITEM',
    'FORMAT',
    'TOO_LONG',
    'TOO_SHORT',
    'TOO_SMALL',
    'INVALID',
    'DUPLICATE_IN_FILE',
    'NOT_FOUND',
    'AMBIGUOUS',
    'PARENT_MISSING',
    'ROOT_INACTIVE',
    'CMD_UNKNOWN',
    'CMD_CHAPTER',
    'UN_UNKNOWN',
    'BR_01',
    'BR_02',
    'BR_11',
    'DATES_ORDER',
    'EXPIRED',
    'NO_COST_LINE',
    'INVOICE_EXISTS',
    'BSD_EXISTS',
    'HISTORY_DATES',
    'INSTALLATION_KIND_REQUIRED',
    'DR_NOT_PERFORMED',
    'NOT_A_CARRIER',
    'MATCHED_ICE',
    'MATCHED_NAME',
    'SAME_NAME_OTHER_ICE',
    'CAP_TREATMENT_REQUIRED',
    'CAP_ALREADY_ACCEPTED',
    'CAP_REPLACED',
    'ROLE_SCOPE',
    'ROLE_NOT_ALLOWED',
    'RETURN_NOT_SHIPPED',
    'RETURN_ALREADY_RECORDED',
    'RETURN_DATES',
    'GAP_COMMENT',
    'REFUSAL_REASON',
    'REFUSED_EXCEEDS',
    'ROLE_EXPIRED',
    'USER_INACTIVE',
    'USER_NOT_MANAGED',
    'NAMES_DIFFER',
    'INVITATIONS_DISABLED',
    'MAPPING_APPLIED',
    'ROW_IGNORED',
    'TICKET_EXISTS',
    'TICKET_APPLIED',
    'NET_MISMATCH',
    'FUTURE_DATE',
    'CLOSED_PERIOD',
    'WEIGHING_NOT_MATCHED',
    'WEIGHING_AMBIGUOUS',
    'WEIGHING_SHARED',
    'NOT_WEIGHABLE',
    'QUANTITY_UNCHANGED',
    'SIGNED_BY_PRODUCER',
    'ALREADY_INVOICED',
    'REPRINT_DOCUMENTS',
    'SIGNATURES_SUPERSEDED',
    'WEIGHING_GAP',
    'EXEMPTION_LOST',
    'GROUP_REJECTED',
    'WRITE_FAILED',
];
/** The kinds of rows a reference points to, for "not found" and "ambiguous" messages. */
const ENTITY_NAMES = {
    waste: { fr: 'Déchet', ar: 'النفاية', en: 'Waste' },
    installation: { fr: 'Installation', ar: 'المنشأة', en: 'Installation' },
    prestataire: { fr: 'Prestataire', ar: 'مقدم الخدمة', en: 'Service provider' },
    conditionnement: { fr: 'Conditionnement', ar: 'التعبئة', en: 'Container' },
    origin: { fr: 'Origine', ar: 'المصدر', en: 'Origin' },
    authorization: { fr: 'Autorisation', ar: 'الترخيص', en: 'Authorization' },
    expedition: { fr: 'BSD', ar: 'سند التتبع', en: 'BSD' },
    company: { fr: 'Société', ar: 'الشركة', en: 'Company' },
    scope: { fr: 'Site ou société', ar: 'الموقع أو الشركة', en: 'Site or company' },
};
/** Formats named by a FORMAT issue. */
const FORMATS = {
    ICE: { fr: '15 chiffres', ar: '15 رقمًا', en: '15 digits' },
    IF: { fr: '7 ou 8 chiffres', ar: '7 أو 8 أرقام', en: '7 or 8 digits' },
    EMAIL: { fr: 'une adresse e-mail', ar: 'عنوان بريد إلكتروني', en: 'an e-mail address' },
    URL: { fr: 'une adresse web', ar: 'عنوان ويب', en: 'a web address' },
    UN: { fr: '4 chiffres', ar: '4 أرقام', en: '4 digits' },
    DR: {
        fr: 'D1 à D15 ou R1 à R13',
        ar: 'من D1 إلى D15 أو من R1 إلى R13',
        en: 'D1 to D15 or R1 to R13',
    },
    CMD: {
        fr: 'un code CMD de 2, 4 ou 6 chiffres',
        ar: 'رمز CMD من 2 أو 4 أو 6 أرقام',
        en: 'a CMD code of 2, 4 or 6 digits',
    },
};
const p = (params, key) => String(params?.[key] ?? '');
const format = (params, locale) => FORMATS[p(params, 'format')]?.[locale] ?? p(params, 'format');
const entity = (params, locale) => ENTITY_NAMES[p(params, 'entity')]?.[locale] ?? p(params, 'entity');
const MESSAGES = {
    RETURN_NOT_SHIPPED: (x) => ({
        fr: `Le BSD ${p(x, 'number')} n'est pas encore enlevé (statut ${p(x, 'status')}) : aucun retour à enregistrer.`,
        ar: `السند ${p(x, 'number')} لم يُرفع بعد (الحالة ${p(x, 'status')}): لا عودة لتسجيلها.`,
        en: `BSD ${p(x, 'number')} has not been picked up yet (status ${p(x, 'status')}): no return to record.`,
    }),
    RETURN_ALREADY_RECORDED: (x) => ({
        fr: `Le BSD ${p(x, 'number')} est déjà ${p(x, 'status')} : la ligne est ignorée.`,
        ar: `السند ${p(x, 'number')} في الحالة ${p(x, 'status')} بالفعل: يُتجاهل السطر.`,
        en: `BSD ${p(x, 'number')} is already ${p(x, 'status')}: the line is skipped.`,
    }),
    RETURN_DATES: (x) => ({
        fr: `La date ${p(x, 'date')} précède ${p(x, 'before')}.`,
        ar: `التاريخ ${p(x, 'date')} يسبق ${p(x, 'before')}.`,
        en: `The date ${p(x, 'date')} is before ${p(x, 'before')}.`,
    }),
    GAP_COMMENT: (x) => ({
        fr: `BR-20 : la quantité reçue s'écarte de ${p(x, 'percent')} % de celle du producteur, au-delà de la tolérance de ${p(x, 'tolerance')} % ; expliquez l'écart en commentaire.`,
        ar: `BR-20: الكمية المستلمة تختلف بنسبة ${p(x, 'percent')}٪ عن كمية المنتج، فوق التسامح البالغ ${p(x, 'tolerance')}٪؛ اشرح الفرق في تعليق.`,
        en: `BR-20: the received quantity differs by ${p(x, 'percent')} % from the producer's, beyond the tolerance of ${p(x, 'tolerance')} %; explain the gap in a comment.`,
    }),
    REFUSAL_REASON: () => ({
        fr: 'Un refus partiel demande un motif.',
        ar: 'الرفض الجزئي يتطلب سببًا.',
        en: 'A partial refusal needs a reason.',
    }),
    REFUSED_EXCEEDS: () => ({
        fr: 'La quantité refusée dépasse la quantité reçue.',
        ar: 'الكمية المرفوضة تتجاوز الكمية المستلمة.',
        en: 'The refused quantity exceeds the received quantity.',
    }),
    FILE_EMPTY: () => ({
        fr: 'La feuille ne contient aucune ligne à importer.',
        ar: 'لا تحتوي الورقة على أي سطر للاستيراد.',
        en: 'The sheet has no line to import.',
    }),
    FILE_TOO_MANY_ROWS: (x) => ({
        fr: `Le fichier dépasse ${p(x, 'max')} lignes ; découpez-le.`,
        ar: `يتجاوز الملف ${p(x, 'max')} سطرًا؛ قسّمه.`,
        en: `The file has more than ${p(x, 'max')} lines; split it.`,
    }),
    MISSING_COLUMN: (x) => ({
        fr: `Colonne obligatoire absente : « ${p(x, 'header')} ».`,
        ar: `عمود إلزامي غير موجود: «${p(x, 'header')}».`,
        en: `Required column missing: "${p(x, 'headerEn') || p(x, 'header')}".`,
    }),
    UNKNOWN_COLUMN: (x) => ({
        fr: `Colonne « ${p(x, 'header')} » non reconnue : elle est ignorée.`,
        ar: `العمود «${p(x, 'header')}» غير معروف: تم تجاهله.`,
        en: `Column "${p(x, 'header')}" not recognised: it is ignored.`,
    }),
    DUPLICATE_COLUMN: (x) => ({
        fr: `Colonne « ${p(x, 'header')} » en double : seule la première est lue.`,
        ar: `العمود «${p(x, 'header')}» مكرر: يُقرأ الأول فقط.`,
        en: `Column "${p(x, 'header')}" appears twice: only the first one is read.`,
    }),
    REQUIRED: () => ({
        fr: 'Valeur obligatoire pour créer la ligne.',
        ar: 'قيمة إلزامية لإنشاء السطر.',
        en: 'Value required to create the line.',
    }),
    NUMBER: () => ({ fr: 'Nombre attendu.', ar: 'يُنتظر رقم.', en: 'A number is expected.' }),
    BOOLEAN: () => ({
        fr: 'Oui ou Non attendu.',
        ar: 'يُنتظر نعم أو لا.',
        en: 'Yes or No expected.',
    }),
    DATE: () => ({
        fr: 'Date attendue, au format JJ/MM/AAAA.',
        ar: 'يُنتظر تاريخ بصيغة يوم/شهر/سنة.',
        en: 'A date is expected, as DD/MM/YYYY.',
    }),
    ENUM: () => ({
        fr: 'Valeur non reconnue : voyez les valeurs admises dans la feuille Instructions.',
        ar: 'قيمة غير معروفة: راجع القيم المقبولة في ورقة التعليمات.',
        en: 'Value not recognised: see the allowed values in the Instructions sheet.',
    }),
    ENUM_ITEM: (x) => ({
        fr: `« ${p(x, 'item')} » n'est pas une valeur admise (feuille Instructions).`,
        ar: `«${p(x, 'item')}» ليست قيمة مقبولة (ورقة التعليمات).`,
        en: `"${p(x, 'item')}" is not an allowed value (Instructions sheet).`,
    }),
    FORMAT: (x) => ({
        fr: `Format attendu : ${format(x, 'fr')}.`,
        ar: `الصيغة المنتظرة: ${format(x, 'ar')}.`,
        en: `Expected format: ${format(x, 'en')}.`,
    }),
    TOO_LONG: (x) => ({
        fr: `Trop long : ${p(x, 'max')} caractères au plus.`,
        ar: `طويل جدًا: ${p(x, 'max')} حرفًا على الأكثر.`,
        en: `Too long: ${p(x, 'max')} characters at most.`,
    }),
    TOO_SHORT: (x) => ({
        fr: `Trop court : ${p(x, 'min')} caractères au moins.`,
        ar: `قصير جدًا: ${p(x, 'min')} أحرف على الأقل.`,
        en: `Too short: ${p(x, 'min')} characters at least.`,
    }),
    TOO_SMALL: (x) => ({
        fr: `Valeur trop petite : au moins ${p(x, 'min')}.`,
        ar: `قيمة صغيرة جدًا: ${p(x, 'min')} على الأقل.`,
        en: `Value too small: at least ${p(x, 'min')}.`,
    }),
    INVALID: (x) => ({
        fr: `Valeur refusée (${p(x, 'detail')}).`,
        ar: `قيمة مرفوضة (${p(x, 'detail')}).`,
        en: `Value refused (${p(x, 'detail')}).`,
    }),
    DUPLICATE_IN_FILE: (x) => ({
        fr: `Même ligne que la ligne ${p(x, 'row')} du fichier : elle est ignorée.`,
        ar: `نفس السطر ${p(x, 'row')} من الملف: تم تجاهله.`,
        en: `Same as line ${p(x, 'row')} of the file: it is ignored.`,
    }),
    NO_COST_LINE: (x) => ({
        fr: `Aucun coût de cette nature pour ce prestataire sur le BSD « ${p(x, 'value')} » : la ligne reste sans rapprochement.`,
        ar: `لا توجد تكلفة من هذا النوع لمقدم الخدمة هذا على السند «${p(x, 'value')}»: يبقى السطر دون مطابقة.`,
        en: `No cost of that kind for this provider on BSD "${p(x, 'value')}": the line stays unmatched.`,
    }),
    BSD_EXISTS: (x) => ({
        fr: `Le BSD « ${p(x, 'value')} » existe déjà sur ce site : la ligne est ignorée.`,
        ar: `السند «${p(x, 'value')}» موجود من قبل في هذا الموقع: تم تجاهل السطر.`,
        en: `BSD "${p(x, 'value')}" already exists on this site: the line is ignored.`,
    }),
    HISTORY_DATES: (x) => ({
        fr: `Dates incohérentes avec le statut (${p(x, 'detail')}).`,
        ar: `التواريخ غير متسقة مع الحالة (${p(x, 'detail')}).`,
        en: `Dates inconsistent with the status (${p(x, 'detail')}).`,
    }),
    INVOICE_EXISTS: (x) => ({
        fr: `La facture « ${p(x, 'value')} » de ce prestataire est déjà enregistrée : la ligne est ignorée.`,
        ar: `الفاتورة «${p(x, 'value')}» لمقدم الخدمة هذا مسجلة من قبل: تم تجاهل السطر.`,
        en: `Invoice "${p(x, 'value')}" of this provider is already recorded: the line is ignored.`,
    }),
    NOT_FOUND: (x) => ({
        fr: `${entity(x, 'fr')} « ${p(x, 'value')} » introuvable.`,
        ar: `${entity(x, 'ar')} «${p(x, 'value')}» غير موجود.`,
        en: `${entity(x, 'en')} "${p(x, 'value')}" not found.`,
    }),
    AMBIGUOUS: (x) => ({
        fr: `Plusieurs lignes « ${entity(x, 'fr')} » correspondent à « ${p(x, 'value')} » : précisez (ICE, ou « Prestataire / Installation »).`,
        ar: `عدة عناصر «${entity(x, 'ar')}» تطابق «${p(x, 'value')}»: حدّد (ICE أو «مقدم الخدمة / المنشأة»).`,
        en: `Several ${entity(x, 'en').toLowerCase()} rows match "${p(x, 'value')}": be more specific (ICE, or "Provider / Installation").`,
    }),
    PARENT_MISSING: (x) => ({
        fr: `L'origine parente « ${p(x, 'path')} » n'existe pas et ne figure pas dans le fichier.`,
        ar: `المصدر الأصل «${p(x, 'path')}» غير موجود وليس في الملف.`,
        en: `The parent origin "${p(x, 'path')}" does not exist and is not in the file.`,
    }),
    ROOT_INACTIVE: () => ({
        fr: 'La racine est le site lui-même : elle ne peut pas être désactivée.',
        ar: 'الجذر هو الموقع نفسه: لا يمكن تعطيله.',
        en: 'The root is the site itself: it cannot be deactivated.',
    }),
    CMD_UNKNOWN: (x) => ({
        fr: `Le code ${p(x, 'code')} n'existe pas dans le Catalogue Marocain des Déchets en vigueur.`,
        ar: `الرمز ${p(x, 'code')} غير موجود في الكتالوج المغربي للنفايات المعمول به.`,
        en: `Code ${p(x, 'code')} is not in the current Moroccan Waste Catalogue.`,
    }),
    CMD_CHAPTER: (x) => ({
        fr: `${p(x, 'code')} est une rubrique : indiquez un code à six chiffres.`,
        ar: `${p(x, 'code')} عنوان باب: أدخل رمزًا من ستة أرقام.`,
        en: `${p(x, 'code')} is a heading: give a six-digit code.`,
    }),
    UN_UNKNOWN: (x) => ({
        fr: `Le n° ONU ${p(x, 'un')} n'est pas dans la table ADR : décrivez ce déchet dans l'application.`,
        ar: `الرقم ${p(x, 'un')} غير موجود في جدول ADR: صِف هذه النفاية في التطبيق.`,
        en: `UN ${p(x, 'un')} is not in the ADR table: describe this waste in the application.`,
    }),
    BR_01: () => ({
        fr: 'Le type ne correspond pas au caractère dangereux du code : justifiez-le (BR-01).',
        ar: 'النوع لا يطابق خطورة الرمز: قدّم مبررًا (BR-01).',
        en: 'The type does not match the hazardous flag of the code: justify it (BR-01).',
    }),
    BR_02: () => ({
        fr: 'Un code en 99 exige une dénomination descriptive d’au moins 10 caractères (BR-02).',
        ar: 'الرمز المنتهي بـ 99 يتطلب تسمية وصفية من 10 أحرف على الأقل (BR-02).',
        en: 'A code ending in 99 needs a descriptive name of at least 10 characters (BR-02).',
    }),
    BR_11: () => ({
        fr: 'Un déchet ADR a besoin d’un n° ONU (BR-11).',
        ar: 'نفاية ADR تحتاج إلى رقم الأمم المتحدة (BR-11).',
        en: 'An ADR waste needs a UN number (BR-11).',
    }),
    DATES_ORDER: () => ({
        fr: 'La date de fin précède la date de début.',
        ar: 'تاريخ النهاية يسبق تاريخ البداية.',
        en: 'The end date is before the start date.',
    }),
    EXPIRED: (x) => ({
        fr: `Document expiré depuis le ${p(x, 'date')} : il est importé pour l'historique.`,
        ar: `وثيقة منتهية الصلاحية منذ ${p(x, 'date')}: تُستورد للأرشيف.`,
        en: `Document expired since ${p(x, 'date')}: it is imported for the record.`,
    }),
    INSTALLATION_KIND_REQUIRED: () => ({
        fr: "Indiquez le type de l'installation pour la créer.",
        ar: 'حدّد نوع المنشأة لإنشائها.',
        en: 'Give the installation kind to create it.',
    }),
    DR_NOT_PERFORMED: (x) => ({
        fr: `L'installation « ${p(x, 'installation')} » ne réalise pas l'opération ${p(x, 'code')}.`,
        ar: `المنشأة «${p(x, 'installation')}» لا تنجز العملية ${p(x, 'code')}.`,
        en: `Installation "${p(x, 'installation')}" does not perform operation ${p(x, 'code')}.`,
    }),
    NOT_A_CARRIER: (x) => ({
        fr: `« ${p(x, 'name')} » n'est pas déclaré comme transporteur.`,
        ar: `«${p(x, 'name')}» غير مصرح به كناقل.`,
        en: `"${p(x, 'name')}" is not declared as a carrier.`,
    }),
    MATCHED_ICE: (x) => ({
        fr: `Prestataire existant reconnu par son ICE : ${p(x, 'name')}.`,
        ar: `تم التعرف على مقدم خدمة موجود عبر ICE: ${p(x, 'name')}.`,
        en: `Existing provider found by its ICE: ${p(x, 'name')}.`,
    }),
    MATCHED_NAME: (x) => ({
        fr: `Prestataire existant reconnu par sa raison sociale et sa ville : ${p(x, 'name')}.`,
        ar: `تم التعرف على مقدم خدمة موجود عبر اسمه ومدينته: ${p(x, 'name')}.`,
        en: `Existing provider found by its company name and city: ${p(x, 'name')}.`,
    }),
    SAME_NAME_OTHER_ICE: (x) => ({
        fr: `Un prestataire « ${p(x, 'name')} » existe avec un autre ICE : un nouveau prestataire sera créé.`,
        ar: `يوجد مقدم خدمة «${p(x, 'name')}» بـ ICE مختلف: سيتم إنشاء مقدم خدمة جديد.`,
        en: `A provider "${p(x, 'name')}" exists with another ICE: a new provider will be created.`,
    }),
    CAP_TREATMENT_REQUIRED: () => ({
        fr: "Indiquez les traitements (codes D/R) pour enregistrer l'acceptation.",
        ar: 'حدّد المعالجات (رموز D/R) لتسجيل القبول.',
        en: 'Give the treatments (D/R codes) to record the acceptance.',
    }),
    CAP_ALREADY_ACCEPTED: () => ({
        fr: "Ce CAP est déjà accepté : la date d'acceptation n'est pas modifiée.",
        ar: 'هذه الشهادة مقبولة مسبقًا: لا يتم تغيير تاريخ القبول.',
        en: 'This CAP is already accepted: the acceptance date is not changed.',
    }),
    CAP_REPLACED: () => ({
        fr: 'Ce CAP a été remplacé par un renouvellement : il ne peut plus être modifié.',
        ar: 'تم استبدال هذه الشهادة بتجديد: لا يمكن تعديلها.',
        en: 'This CAP was replaced by a renewal: it can no longer be changed.',
    }),
    ROLE_SCOPE: (x) => ({
        fr: `Le rôle « ${p(x, 'role')} » ne s'attribue pas sur ce périmètre.`,
        ar: `الدور «${p(x, 'role')}» لا يُسند على هذا النطاق.`,
        en: `The role "${p(x, 'role')}" cannot be granted on this scope.`,
    }),
    ROLE_NOT_ALLOWED: (x) => ({
        fr: `Vous ne pouvez pas attribuer le rôle « ${p(x, 'role')} » sur ce périmètre.`,
        ar: `لا يمكنك إسناد الدور «${p(x, 'role')}» على هذا النطاق.`,
        en: `You cannot grant the role "${p(x, 'role')}" on this scope.`,
    }),
    ROLE_EXPIRED: () => ({
        fr: 'La date de fin est déjà passée : le rôle ne donnerait aucun accès.',
        ar: 'تاريخ النهاية قد مضى: لن يمنح الدور أي وصول.',
        en: 'The end date has passed: the role would give no access.',
    }),
    USER_INACTIVE: () => ({
        fr: 'Cet utilisateur est désactivé : ses rôles sont ajoutés mais il ne peut pas se connecter.',
        ar: 'هذا المستخدم معطل: تضاف أدواره لكنه لا يستطيع الدخول.',
        en: 'This user is deactivated: the roles are added but they cannot sign in.',
    }),
    USER_NOT_MANAGED: () => ({
        fr: "Cet utilisateur existe déjà et vous ne gérez aucun de ses rôles : l'administrateur de l'organisation peut les modifier.",
        ar: 'هذا المستخدم موجود مسبقًا ولا تدير أيًا من أدواره: يمكن لمسؤول المنظمة تعديلها.',
        en: 'This user already exists and you manage none of their roles: the organisation administrator can change them.',
    }),
    NAMES_DIFFER: () => ({
        fr: "Le prénom, le nom ou la langue diffèrent d'une ligne à l'autre : la première ligne fait foi.",
        ar: 'الاسم أو اللقب أو اللغة تختلف بين الأسطر: يُعتمد السطر الأول.',
        en: 'First name, last name or language differ between lines: the first line prevails.',
    }),
    INVITATIONS_DISABLED: () => ({
        fr: "L'envoi des invitations n'est pas configuré : les nouveaux utilisateurs ne peuvent pas être créés.",
        ar: 'إرسال الدعوات غير مهيأ: لا يمكن إنشاء مستخدمين جدد.',
        en: 'Invitations are not configured: new users cannot be created.',
    }),
    MAPPING_APPLIED: () => ({
        fr: 'Les colonnes du fichier sont lues avec la correspondance enregistrée pour ce site ; « Changer la correspondance » la modifie.',
        ar: 'قُرئت أعمدة الملف بالمطابقة المسجلة لهذا الموقع؛ «تغيير المطابقة» يعدّلها.',
        en: 'The columns of the file are read with the mapping saved for this site; "Change the mapping" edits it.',
    }),
    ROW_IGNORED: (x) => ({
        fr: `Le produit « ${p(x, 'value')} » est ignoré par la correspondance : la ligne n'est pas importée.`,
        ar: `المنتَج «${p(x, 'value')}» متجاهَل في المطابقة: لا يُستورد السطر.`,
        en: `Product "${p(x, 'value')}" is ignored by the mapping: the line is not imported.`,
    }),
    TICKET_EXISTS: (x) => ({
        fr: `Le ticket ${p(x, 'ticket')} est déjà en stock (${p(x, 'waste')}, pesée du ${p(x, 'date')}) : la ligne est ignorée.`,
        ar: `الوصل ${p(x, 'ticket')} مسجّل في المخزون من قبل (${p(x, 'waste')}، وزن بتاريخ ${p(x, 'date')}): يُتجاهل السطر.`,
        en: `Ticket ${p(x, 'ticket')} is already in stock (${p(x, 'waste')}, weighed on ${p(x, 'date')}): the line is skipped.`,
    }),
    TICKET_APPLIED: (x) => ({
        fr: `Le ticket ${p(x, 'ticket')} a déjà été importé sur le BSD ${p(x, 'number')} (pesée du ${p(x, 'date')}) : la ligne est ignorée, même si la quantité a été corrigée depuis.`,
        ar: `الوصل ${p(x, 'ticket')} مستورد من قبل في السند ${p(x, 'number')} (وزن بتاريخ ${p(x, 'date')}): يُتجاهل السطر، ولو صُحّحت الكمية بعد ذلك.`,
        en: `Ticket ${p(x, 'ticket')} was already imported on BSD ${p(x, 'number')} (weighed on ${p(x, 'date')}): the line is skipped, even if the quantity was corrected since.`,
    }),
    NET_MISMATCH: (x) => ({
        fr: `Le poids net ${p(x, 'net')} diffère du brut moins la tare (${p(x, 'computed')}) : le net est retenu.`,
        ar: `الوزن الصافي ${p(x, 'net')} يختلف عن الإجمالي ناقص الوزن الفارغ (${p(x, 'computed')}): يُعتمد الصافي.`,
        en: `The net weight ${p(x, 'net')} differs from the gross minus the tare (${p(x, 'computed')}): the net is used.`,
    }),
    FUTURE_DATE: () => ({
        fr: 'La date de pesée est dans le futur (BR-12).',
        ar: 'تاريخ الوزن في المستقبل (BR-12).',
        en: 'The weighing date is in the future (BR-12).',
    }),
    CLOSED_PERIOD: (x) => ({
        fr: `Le registre est clôturé du ${p(x, 'from')} au ${p(x, 'to')} : l'entrée y figurera comme une ligne ajoutée après la clôture.`,
        ar: `السجل مُغلق من ${p(x, 'from')} إلى ${p(x, 'to')}: سيظهر المدخل سطرًا مضافًا بعد الإغلاق.`,
        en: `The registre is closed from ${p(x, 'from')} to ${p(x, 'to')}: the entry will show as a line added after the closure.`,
    }),
    WEIGHING_NOT_MATCHED: (x) => ({
        fr: `Aucun BSD du site ne part avec le camion ${p(x, 'plate')} autour du ${p(x, 'date')} : indiquez le n° de BSD.`,
        ar: `لا يغادر أي سند للموقع مع الشاحنة ${p(x, 'plate')} في حدود ${p(x, 'date')}: أدخل رقم السند.`,
        en: `No BSD of the site leaves with truck ${p(x, 'plate')} around ${p(x, 'date')}: give the BSD number.`,
    }),
    WEIGHING_AMBIGUOUS: (x) => ({
        fr: `Plusieurs BSD correspondent à « ${p(x, 'value')} », et une pesée ne se répartit pas entre plusieurs BSD : indiquez le n° de BSD du site.`,
        ar: `عدة سندات تطابق «${p(x, 'value')}»، ولا يُوزَّع وزن واحد على عدة سندات: أدخل رقم سند الموقع.`,
        en: `Several BSDs match "${p(x, 'value')}", and one weighing cannot be split between BSDs: give the site's BSD number.`,
    }),
    WEIGHING_SHARED: (x) => ({
        fr: `Le BSD ${p(x, 'number')} part dans le même camion que ${p(x, 'count')} autre(s) BSD, et le poids d'un camion ne se répartit pas entre plusieurs BSD : saisissez leurs quantités sur le chargement.`,
        ar: `السند ${p(x, 'number')} يغادر في الشاحنة نفسها مع ${p(x, 'count')} سند(ات) أخرى، ولا يُوزَّع وزن شاحنة على عدة سندات: أدخل كمياتها في الشحنة.`,
        en: `BSD ${p(x, 'number')} leaves in the same truck as ${p(x, 'count')} other BSD(s), and one truck's weight cannot be split between BSDs: enter their quantities on the chargement.`,
    }),
    NOT_WEIGHABLE: (x) => ({
        fr: `Le BSD ${p(x, 'number')} est au statut ${p(x, 'status')} : sa quantité ne prend pas de pesée.`,
        ar: `السند ${p(x, 'number')} في الحالة ${p(x, 'status')}: لا تقبل كميته وزنًا.`,
        en: `BSD ${p(x, 'number')} is ${p(x, 'status')}: its quantity takes no weighing.`,
    }),
    QUANTITY_UNCHANGED: (x) => ({
        fr: `Le BSD ${p(x, 'number')} porte déjà cette quantité réelle : rien à changer.`,
        ar: `السند ${p(x, 'number')} يحمل هذه الكمية الحقيقية من قبل: لا شيء للتغيير.`,
        en: `BSD ${p(x, 'number')} already has this real quantity: nothing to change.`,
    }),
    SIGNED_BY_PRODUCER: (x) => ({
        fr: `Le producteur a signé la quantité du BSD ${p(x, 'number')} : retirez sa signature avant d'importer la pesée.`,
        ar: `وقّع المنتج على كمية السند ${p(x, 'number')}: اسحب توقيعه قبل استيراد الوزن.`,
        en: `The producer signed the quantity of BSD ${p(x, 'number')}: withdraw the signature before importing the weighing.`,
    }),
    ALREADY_INVOICED: (x) => ({
        fr: `Un coût du BSD ${p(x, 'number')} est déjà facturé : la pesée n'est pas importée ; révisez la quantité depuis le BSD si la facture doit être reprise.`,
        ar: `تكلفة من تكاليف السند ${p(x, 'number')} مفوترة من قبل: لا يُستورد الوزن؛ راجع الكمية من السند إذا لزم تعديل الفاتورة.`,
        en: `A cost of BSD ${p(x, 'number')} is already invoiced: the weighing is not imported; revise the quantity from the BSD if the invoice must be reworked.`,
    }),
    REPRINT_DOCUMENTS: (x) => ({
        fr: `Le BSD ${p(x, 'number')} n'est pas encore parti : imprimez de nouveau le BSD, les étiquettes et la déclaration avant l'enlèvement.`,
        ar: `السند ${p(x, 'number')} لم يُرفع بعد: أعد طباعة السند والملصقات والتصريح قبل الرفع.`,
        en: `BSD ${p(x, 'number')} has not left yet: print the BSD, the labels and the declaration again before the pickup.`,
    }),
    SIGNATURES_SUPERSEDED: (x) => ({
        fr: `Les signatures du BSD ${p(x, 'number')} seront à refaire : la révision en fait une nouvelle version.`,
        ar: `يجب إعادة توقيعات السند ${p(x, 'number')}: المراجعة تُنشئ نسخة جديدة منه.`,
        en: `The signatures of BSD ${p(x, 'number')} will have to be given again: the revision makes a new version.`,
    }),
    WEIGHING_GAP: (x) => ({
        fr: `La pesée s'écarte de ${p(x, 'percent')} % de la quantité enregistrée, au-delà de la tolérance de ${p(x, 'tolerance')} % du site.`,
        ar: `يختلف الوزن بنسبة ${p(x, 'percent')}٪ عن الكمية المسجلة، فوق تسامح الموقع البالغ ${p(x, 'tolerance')}٪.`,
        en: `The weighing differs by ${p(x, 'percent')} % from the recorded quantity, beyond the site's tolerance of ${p(x, 'tolerance')} %.`,
    }),
    EXEMPTION_LOST: (x) => ({
        fr: `Avec la quantité pesée, l'exemption du 1.1.3.6 ne tient plus (${p(x, 'points')} points pour ${p(x, 'threshold')}) : elle est retirée et les consignes écrites doivent être imprimées.`,
        ar: `بالكمية الموزونة لم يعد إعفاء الفقرة 1.1.3.6 قائمًا (${p(x, 'points')} نقطة من ${p(x, 'threshold')}): يُسحب الإعفاء ويجب طباعة التعليمات المكتوبة.`,
        en: `With the weighed quantity the 1.1.3.6 exemption no longer holds (${p(x, 'points')} points for ${p(x, 'threshold')}): it is withdrawn and the written instructions must be printed.`,
    }),
    GROUP_REJECTED: (x) => ({
        fr: `Écrite avec la ligne ${p(x, 'rows')}, qui est rejetée : corrigez-la d'abord.`,
        ar: `تُكتب مع السطر ${p(x, 'rows')} المرفوض: صحّحه أولًا.`,
        en: `Written with line ${p(x, 'rows')}, which is rejected: fix it first.`,
    }),
    WRITE_FAILED: (x) => ({
        fr: `Écriture refusée : ${p(x, 'detail')}`,
        ar: `تم رفض الكتابة: ${p(x, 'detail')}`,
        en: `Write refused: ${p(x, 'detail')}`,
    }),
};
export function importIssueText(issue, locale = 'fr') {
    return MESSAGES[issue.code](issue.params)[locale];
}
export const IMPORT_KIND_LABELS = {
    ORIGINS: { fr: 'Origines', ar: 'المصادر', en: 'Origins' },
    WASTES: { fr: 'Déchets', ar: 'النفايات', en: 'Wastes' },
    CONDITIONNEMENTS: { fr: 'Conditionnements', ar: 'التعبئة', en: 'Containers' },
    PRESTATAIRES: {
        fr: 'Prestataires et installations',
        ar: 'مقدمو الخدمات والمنشآت',
        en: 'Service providers and installations',
    },
    AUTHORIZATIONS: { fr: 'Autorisations', ar: 'التراخيص', en: 'Authorizations' },
    CAPS: { fr: 'CAP', ar: 'شهادات القبول المسبق', en: 'CAP' },
    FILIERES: { fr: 'Filières par défaut', ar: 'المسارات الافتراضية', en: 'Default routes' },
    USERS: { fr: 'Utilisateurs', ar: 'المستخدمون', en: 'Users' },
    RETURNS: {
        fr: 'Retours de BSD (relevé)',
        ar: 'عودة السندات (كشف)',
        en: 'BSD returns (statement)',
    },
    INVOICES: { fr: 'Factures prestataires', ar: 'فواتير مقدمي الخدمات', en: 'Provider invoices' },
    EXPEDITIONS: {
        fr: 'Expéditions (historique)',
        ar: 'الشحنات (السجل التاريخي)',
        en: 'Expéditions (history)',
    },
    STOCK_WEIGHINGS: {
        fr: 'Pesées de production (pont-bascule)',
        ar: 'أوزان الإنتاج (ميزان الشاحنات)',
        en: 'Production weighings (weighbridge)',
    },
    EXPEDITION_WEIGHINGS: {
        fr: 'Pesées des expéditions (pont-bascule)',
        ar: 'أوزان الشحنات (ميزان الشاحنات)',
        en: 'Expédition weighings (weighbridge)',
    },
};
export const IMPORT_ACTION_LABELS = {
    CREATE: { fr: 'Créée', ar: 'أُنشئ', en: 'Created' },
    UPDATE: { fr: 'Mise à jour', ar: 'حُدّث', en: 'Updated' },
    UNCHANGED: { fr: 'Inchangée', ar: 'دون تغيير', en: 'Unchanged' },
    ERROR: { fr: 'Rejetée', ar: 'مرفوض', en: 'Rejected' },
};
// ---- API contract ------------------------------------------------------------------------------
const uuid = z.string().uuid();
export const importKindSchema = z.enum(IMPORT_KINDS);
export const importUploadQuerySchema = z.object({
    kind: importKindSchema,
    siteId: uuid.optional(),
    lang: z.enum(IMPORT_LANGUAGES).default('fr'),
    /** The code page of a CSV export, when its detection cannot tell (Windows-1256 for Arabic). */
    encoding: z.enum(IMPORT_ENCODINGS).optional(),
});
export const importTemplateQuerySchema = z.object({
    siteId: uuid.optional(),
    lang: z.enum(IMPORT_LANGUAGES).default('fr'),
});
export const importListQuerySchema = z.object({
    siteId: uuid.optional(),
    kind: importKindSchema.optional(),
});
export const importReportQuerySchema = z.object({
    lang: z.enum(LOCALES).default('fr'),
});
export const importReverseSchema = z.object({
    reason: z.string().trim().min(5).max(500),
});
//# sourceMappingURL=imports.js.map